import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useInvoice } from '../context/InvoiceContext';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const formatTZ = (date, timeZone) =>
  new Intl.DateTimeFormat('en-US', { timeZone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).format(date);

const formatDateTZ = (date, timeZone) =>
  new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', day: '2-digit', month: 'short' }).format(date);

const CLOCKS = [
  { code: 'de', label: 'DE', city: 'Berlin',   tz: 'Europe/Berlin', lat: 52.52,  lon: 13.41  },
  { code: 'in', label: 'IN', city: 'Simmakkal', tz: 'Asia/Kolkata',  lat: 9.9202, lon: 78.1113 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
// Twemoji SVG via jsDelivr — consistent, high-quality flag rendering on all platforms.
// Codepoints: regional indicator base is 0x1F1A5 + charCode('A'...'Z')
const FlagImg = ({ code, size = 20 }) => {
  const [imgError, setImgError] = useState(false);
  const upper = code.toUpperCase();
  const emoji = [...upper].map(c => String.fromCodePoint(0x1F1A5 + c.charCodeAt(0))).join('');
  const cpHex = [...upper].map(c => (0x1F1A5 + c.charCodeAt(0)).toString(16)).join('-');
  const src = `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${cpHex}.svg`;

  if (imgError) {
    return <span style={{ fontSize: '18px', lineHeight: 1, flexShrink: 0, display: 'block' }}>{emoji}</span>;
  }
  return (
    <img
      src={src}
      width={size}
      height={Math.round(size * 0.75)}
      alt={emoji}
      onError={() => setImgError(true)}
      style={{ display: 'block', flexShrink: 0, borderRadius: '2px' }}
    />
  );
};

const getHourInTZ = (date, tz) =>
  parseInt(new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false }).format(date), 10);

const getOffsetMinutes = (date, tz) => {
  const local = new Date(date.toLocaleString('en-US', { timeZone: tz }));
  const utc   = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  return (local - utc) / 60000;
};

const formatDiff = (mins) => {
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${mins >= 0 ? '+' : '-'}${h}h${m ? ` ${m}m` : ''}`;
};

// ── Icons ─────────────────────────────────────────────────────────────────────
const DayNightIcon = ({ hour }) => {
  const isDay = hour >= 6 && hour < 20;
  return isDay ? (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="4.5" fill="#F59E0B" />
      {[0,45,90,135,180,225,270,315].map(deg => {
        const r = deg * Math.PI / 180;
        return <line key={deg} x1={12+7*Math.sin(r)} y1={12-7*Math.cos(r)} x2={12+9.5*Math.sin(r)} y2={12-9.5*Math.cos(r)} stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round" />;
      })}
    </svg>
  ) : (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill="#818CF8" />
    </svg>
  );
};

const weatherInfo = (code) => {
  if (code === 0)   return { color: '#F59E0B', type: 'sun' };
  if (code <= 2)    return { color: '#94A3B8', type: 'partcloud' };
  if (code <= 3)    return { color: '#64748B', type: 'cloud' };
  if (code <= 48)   return { color: '#94A3B8', type: 'fog' };
  if (code <= 55)   return { color: '#60A5FA', type: 'rain' };
  if (code <= 65)   return { color: '#3B82F6', type: 'rain' };
  if (code <= 75)   return { color: '#BAE6FD', type: 'snow' };
  if (code <= 82)   return { color: '#3B82F6', type: 'rain' };
  return                  { color: '#8B5CF6', type: 'storm' };
};

const aqiInfo = (aqi) => {
  if (aqi <= 20)  return { color: '#22C55E', label: 'Good' };
  if (aqi <= 40)  return { color: '#84CC16', label: 'Fair' };
  if (aqi <= 60)  return { color: '#EAB308', label: 'Moderate' };
  if (aqi <= 80)  return { color: '#F97316', label: 'Poor' };
  if (aqi <= 100) return { color: '#EF4444', label: 'Very Poor' };
  return          { color: '#8B5CF6', label: 'Hazardous' };
};

const WeatherIcon = ({ type, color, size = 12 }) => {
  const s = { flexShrink: 0 };
  if (type === 'sun') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={s}>
      <circle cx="12" cy="12" r="4.5" fill={color} />
      {[0,60,120,180,240,300].map(deg => {
        const r = deg * Math.PI / 180;
        return <line key={deg} x1={12+7*Math.sin(r)} y1={12-7*Math.cos(r)} x2={12+9.5*Math.sin(r)} y2={12-9.5*Math.cos(r)} stroke={color} strokeWidth="1.8" strokeLinecap="round" />;
      })}
    </svg>
  );
  if (type === 'partcloud') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={s}>
      <circle cx="9" cy="9" r="3.5" fill="#F59E0B" />
      <rect x="4" y="13" width="16" height="6" rx="3" fill={color} />
    </svg>
  );
  if (type === 'cloud') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={s}>
      <rect x="3" y="10" width="18" height="8" rx="4" fill={color} />
    </svg>
  );
  if (type === 'fog') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={s}>
      {[8,12,16].map(y => <line key={y} x1="3" y1={y} x2="21" y2={y} stroke={color} strokeWidth="2" strokeLinecap="round" />)}
    </svg>
  );
  if (type === 'rain') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={s}>
      <rect x="3" y="4" width="18" height="8" rx="4" fill={color} />
      {[7,12,17].map(x => <line key={x} x1={x} y1="15" x2={x-2} y2="21" stroke={color} strokeWidth="2" strokeLinecap="round" />)}
    </svg>
  );
  if (type === 'snow') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={s}>
      <rect x="3" y="4" width="18" height="7" rx="3" fill="#94A3B8" />
      {[7,12,17].map(x => <circle key={x} cx={x} cy="19" r="1.5" fill={color} />)}
    </svg>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={s}>
      <rect x="3" y="3" width="18" height="8" rx="4" fill="#64748B" />
      <path d="M13 13l-3 5h4l-3 4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ── Weather fetch ─────────────────────────────────────────────────────────────
const fetchWeather = async (lat, lon) => {
  const [wRes, aqiRes] = await Promise.all([
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,apparent_temperature,weather_code,relative_humidity_2m&timezone=auto`),
    fetch(`https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lon}&current=european_aqi`)
  ]);
  const [wData, aqiData] = await Promise.all([wRes.json(), aqiRes.json()]);
  return {
    temp:      Math.round(wData.current.temperature_2m),
    feelsLike: Math.round(wData.current.apparent_temperature),
    code:      wData.current.weather_code,
    humidity:  wData.current.relative_humidity_2m,
    aqi:       Math.round(aqiData.current?.european_aqi ?? 0),
  };
};

// ── WorldClock ────────────────────────────────────────────────────────────────
const WorldClock = () => {
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState({});

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const load = async () => {
      const results = await Promise.allSettled(
        CLOCKS.map(({ lat, lon, tz }) => fetchWeather(lat, lon).then(w => ({ tz, ...w })))
      );
      const map = {};
      results.forEach(r => { if (r.status === 'fulfilled') map[r.value.tz] = r.value; });
      setWeather(map);
    };
    load();
    const id = setInterval(load, 15 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const [deOff, inOff] = CLOCKS.map(({ tz }) => getOffsetMinutes(now, tz));
  const diffMins = inOff - deOff;

  const cardBg    = 'var(--color-gray-50)';
  const cardBorder= '1px solid var(--color-gray-100)';
  const labelColor= 'var(--color-text-primary)';
  const mutedColor= 'var(--color-text-secondary)';
  const timeColor = 'var(--color-primary)';

  return (
    <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
      {CLOCKS.map(({ code, label, city, tz }) => {
        const hour = getHourInTZ(now, tz);
        const w    = weather[tz];
        const wi   = w ? weatherInfo(w.code) : null;
        const ai   = w ? aqiInfo(w.aqi) : null;

        return (
          <div key={tz} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '5px 10px',
            borderRadius: 'var(--radius-sm)',
            backgroundColor: cardBg,
            border: cardBorder,
          }}>
            <FlagImg code={code} size={18} />
            <div style={{ lineHeight: 1.4 }}>
              {/* Row 1 — label · city · date · day/night */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: mutedColor }}>
                <span style={{ fontWeight: 600, color: labelColor }}>{label}</span>
                <span>·</span>
                <span style={{ fontWeight: 500, color: labelColor }}>{city}</span>
                <span>·</span>
                <span>{formatDateTZ(now, tz)}</span>
                <DayNightIcon hour={hour} />
              </div>
              {/* Row 2 — time */}
              <div style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '1px', color: timeColor }}>
                {formatTZ(now, tz)}
              </div>
              {/* Row 3 — weather */}
              {wi && w && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', marginTop: '1px', color: mutedColor }}>
                  <WeatherIcon type={wi.type} color={wi.color} size={11} />
                  <span style={{ color: labelColor, fontWeight: 600 }}>{w.temp}°C</span>
                  <span>feels {w.feelsLike}°</span>
                  <span style={{ opacity: 0.4 }}>·</span>
                  <span style={{ color: '#60A5FA' }}>💧{w.humidity}%</span>
                  <span style={{ opacity: 0.4 }}>·</span>
                  {ai && (
                    <span style={{
                      backgroundColor: ai.color + '28',
                      color: ai.color,
                      fontWeight: 700,
                      padding: '0 5px',
                      borderRadius: '2px',
                    }}>
                      AQI {w.aqi}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* DST-aware offset pill */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '4px 8px',
        borderRadius: 'var(--radius-sm)',
        backgroundColor: cardBg,
        border: cardBorder,
        lineHeight: 1.4,
      }}>
        <span style={{ fontSize: '9px', color: mutedColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>offset</span>
        <span style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: labelColor, fontFamily: 'monospace' }}>
          {formatDiff(diffMins)}
        </span>
      </div>
    </div>
  );
};

// ── UserPanel ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const UserPanel = ({ open, onClose, currentUser, onLogout, quizRunning }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  const roleLabel =
    currentUser?.role === 'super_admin' ? 'Super Admin'
    : currentUser?.role === 'admin' ? 'Admin'
    : 'Employee';

  return (
    <>
      {open && (
        <div onClick={onClose} style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.35)',
          zIndex: 'var(--z-modal-backdrop)',
        }} />
      )}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '260px',
        backgroundColor: 'var(--color-white)',
        boxShadow: 'var(--shadow-xl)',
        zIndex: 'var(--z-modal)',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.22s ease',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--spacing-md) var(--spacing-lg)',
          borderBottom: '1px solid var(--color-gray-100)',
        }}>
          <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>Account</span>
          <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
          <div>
            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--spacing-xs)', color: 'var(--color-text-primary)' }}>
              {currentUser?.firstName} {currentUser?.lastName}
            </div>
            <span className="badge badge-primary" style={{ marginBottom: 'var(--spacing-xs)', display: 'inline-block' }}>
              {roleLabel}
            </span>
            {currentUser?.loginId && (
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                Login ID: {currentUser.loginId}
              </div>
            )}
          </div>

          {!quizRunning && (
            <>
              <hr style={{ border: 'none', borderTop: '1px solid var(--color-gray-100)', margin: 0 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                <Link
                  to="/profile"
                  onClick={onClose}
                  className={`btn ${isActive('/profile') ? 'btn-primary' : 'btn-secondary'} btn-block`}
                  style={{ textDecoration: 'none', textAlign: 'center' }}
                >
                  Settings
                </Link>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: 'var(--spacing-md) var(--spacing-lg)', borderTop: '1px solid var(--color-gray-100)' }}>
          <button onClick={onLogout} className="btn btn-outline btn-block">Logout</button>
        </div>
      </div>
    </>
  );
};


// ── Notifications ─────────────────────────────────────────────────────────────
const formatTimeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const formatNotifMessage = (n) => {
  const actor = n.User ? (n.User.loginId ? n.User.loginId.charAt(0).toUpperCase() + n.User.loginId.slice(1) : n.User.firstName) : 'Someone';
  const et = n.details?.entityType || 'invoice';
  const action = n.action;
  if (et === 'invoice') {
    const vendor = n.details?.vendorName || n.details?.vendor || '';
    if (action === 'create') return `${actor} added expense${vendor ? ` — ${vendor}` : ''}`;
    if (action === 'update') return `${actor} updated expense${vendor ? ` — ${vendor}` : ''}`;
    if (action === 'delete') return `${actor} deleted expense${vendor ? ` — ${vendor}` : ''}`;
    if (action === 'download') return `${actor} downloaded invoice`;
  }
  if (et === 'timesheet') {
    const date = n.details?.date || '';
    const fmtDate = d => {
      if (!d) return '';
      const today = new Date().toISOString().slice(0, 10);
      const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      if (d === today) return 'today';
      if (d === yesterday) return 'yesterday';
      return d;
    };
    if (action === 'create') return `${actor} checked-in ${fmtDate(date)}`;
    if (action === 'update') {
      const changes = n.details?.changes || {};
      if (changes.checkOut) return `${actor} checked-out ${fmtDate(date)}`;
      if (changes.checkIn) return `${actor} checked-in ${fmtDate(date)}`;
      return `${actor} updated timesheet ${fmtDate(date)}`;
    }
    if (action === 'delete') return `${actor} deleted timesheet ${fmtDate(date)}`;
  }
  if (et === 'todo') {
    const title = n.details?.title ? `"${n.details.title}"` : 'a todo';
    const statusLabel = { todo: 'Todo', in_progress: 'In Progress', blocked: 'Blocked', in_review: 'In Review', completed: 'Done' };
    if (action === 'create') return `${actor} created todo ${title}`;
    if (action === 'delete') return `${actor} deleted todo ${title}`;
    if (action === 'update') {
      const prev = n.details?.prevStatus;
      const next = n.details?.status;
      if (prev && next && prev !== next)
        return `${actor} moved ${title} → ${statusLabel[next] || next}`;
      return `${actor} updated todo ${title}`;
    }
  }
  if (et === 'vacation') {
    if (action === 'create') return `${actor} requested leave (${n.details?.startDate} – ${n.details?.endDate})`;
  }
  if (et === 'voice_note') {
    const leadName = n.details?.leadName ? ` for ${n.details.leadName}` : '';
    return `${actor} added a voice recording${leadName}`;
  }
  if (et === 'follow_up') {
    const leadName = n.details?.leadName || 'a lead';
    return `Follow up with ${leadName} today`;
  }
  if (et === 'installment') {
    const title = n.details?.title || 'Installment reminder';
    const desc = n.details?.description;
    return desc ? `${title} — ${desc}` : title;
  }
  return `${actor} ${action}`;
};

const getNotifLink = (n) => {
  const et = n.details?.entityType;
  if (et === 'timesheet') return '/work/timesheets';
  if (et === 'todo')      return '/work/todos';
  if (et === 'vacation')  return '/work/leave';
  if (et === 'voice_note') return '/leads';
  if (et === 'follow_up') return '/leads';
  if (et === 'installment') return '/work/reminders';
  return '/dashboard';
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState(() => localStorage.getItem('notifLastSeen') || null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);

  const fetchNotifs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setNotifications(data.notifications);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifs();
    const id = setInterval(fetchNotifs, 60000);
    return () => clearInterval(id);
  }, [fetchNotifs]);

  // Close dropdown on outside click (covers both bell and portal dropdown)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        bellRef.current && !bellRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const unreadCount = lastSeenAt
    ? notifications.filter(n => new Date(n.createdAt) > new Date(lastSeenAt)).length
    : notifications.length;

  const handleOpen = () => {
    if (!open) {
      const rect = bellRef.current?.getBoundingClientRect();
      if (rect) {
        const dropdownWidth = Math.min(340, window.innerWidth - 24);
        const left = Math.max(12, Math.min(rect.left, window.innerWidth - dropdownWidth - 12));
        setDropdownPos({ top: rect.bottom + 8, left });
      }
      const now = new Date().toISOString();
      setLastSeenAt(now);
      localStorage.setItem('notifLastSeen', now);
    }
    setOpen(o => !o);
  };

  const mutedColor = 'var(--color-text-secondary)';
  const bgColor = '#fff';
  const borderColor = 'var(--color-gray-200)';
  const textColor = 'var(--color-text-primary)';

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const notifItems = (
    <>
      <div style={{
        padding: isMobile ? '14px 16px' : '10px 14px',
        borderBottom: `1px solid ${borderColor}`,
        fontWeight: 'var(--font-weight-semibold)',
        fontSize: 'var(--font-size-sm)',
        color: textColor,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>Activity</span>
        {isMobile && (
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: mutedColor, fontSize: '18px', lineHeight: 1 }}>✕</button>
        )}
      </div>
      <div style={{ overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
        {notifications.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: mutedColor, fontSize: 'var(--font-size-sm)' }}>
            No recent activity
          </div>
        ) : (
          notifications.map(n => {
            const isUnread = lastSeenAt ? new Date(n.createdAt) > new Date(lastSeenAt) : false;
            return (
              <div key={n.id} onClick={() => { navigate(getNotifLink(n)); setOpen(false); }} style={{
                padding: isMobile ? '14px 16px' : '10px 14px',
                borderBottom: `1px solid ${borderColor}`,
                backgroundColor: isUnread ? 'rgba(183,28,28,0.04)' : 'transparent',
                display: 'flex', flexDirection: 'column', gap: '3px',
                cursor: 'pointer',
              }}>
                <div style={{ fontSize: 'var(--font-size-sm)', color: textColor, lineHeight: 1.5 }}>
                  {isUnread && (
                    <span style={{
                      display: 'inline-block', width: '7px', height: '7px',
                      borderRadius: '50%', background: 'var(--color-primary)',
                      marginRight: '7px', verticalAlign: 'middle', flexShrink: 0,
                    }} />
                  )}
                  {formatNotifMessage(n)}
                </div>
                <div style={{ fontSize: '11px', color: mutedColor, paddingLeft: isUnread ? '14px' : '0' }}>
                  {formatTimeAgo(n.createdAt)} · {new Date(n.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );

  const dropdown = open ? createPortal(
    isMobile ? (
      <>
        {/* Backdrop */}
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 99998,
          }}
        />
        {/* Bottom sheet */}
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            maxHeight: '75vh',
            backgroundColor: bgColor,
            borderRadius: '16px 16px 0 0',
            boxShadow: '0 -4px 32px rgba(0,0,0,0.22)',
            zIndex: 99999,
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Drag handle */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
            <div style={{ width: '36px', height: '4px', borderRadius: '2px', backgroundColor: borderColor }} />
          </div>
          {notifItems}
        </div>
      </>
    ) : (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: `${dropdownPos.top}px`,
          left: `${dropdownPos.left}px`,
          width: '340px',
          maxHeight: '420px',
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          zIndex: 99999,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {notifItems}
      </div>
    ),
    document.body
  ) : null;

  return (
    <>
      <button
        ref={bellRef}
        onClick={handleOpen}
        title="Notifications"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', borderRadius: 'var(--radius-full)', flexShrink: 0,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#78746F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: '1px', right: '1px',
            background: 'var(--color-primary)', color: '#fff',
            borderRadius: '50%', width: '16px', height: '16px',
            fontSize: '10px', fontWeight: '700',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1,
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {dropdown}
    </>
  );
};

// ── Header ────────────────────────────────────────────────────────────────────
const Header = ({ onToggleSidebar, sidebarCollapsed, onToggleCollapse }) => {
  const { branding, impersonating, stopImpersonation } = useInvoice();

  return (
    <>
      <header style={{
        backgroundColor: 'var(--color-white)',
        boxShadow: 'var(--shadow-sm)',
        position: 'sticky',
        top: 0,
        zIndex: 'var(--z-sticky)',
        transition: 'background-color 0.4s ease',
      }}>
        <div className="container" style={{ padding: 'var(--spacing-md)' }}>
          <div className="flex-between" style={{ gap: 'var(--spacing-md)' }}>

            {/* Hamburger — mobile only */}
            <button
              className="show-mobile"
              onClick={onToggleSidebar}
              style={{
                display: 'none',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '6px', flexShrink: 0,
                color: '#44403C',
              }}
              aria-label="Toggle menu"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>

            {/* Sidebar collapse toggle — desktop only, pinned to the viewport's top-left corner */}
            <button
              className="hide-mobile"
              onClick={onToggleCollapse}
              style={{
                position: 'fixed', top: '10px', left: '10px', zIndex: 'var(--z-sticky, 100)',
                background: 'var(--color-white)', border: '1px solid #EAE7E4', cursor: 'pointer',
                borderRadius: '6px', boxShadow: 'var(--shadow-sm)',
                padding: '6px',
                color: '#44403C',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
              aria-label={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
              title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <line x1="9" y1="4" x2="9" y2="20" />
                {sidebarCollapsed
                  ? <polyline points="13,9 16,12 13,15" />
                  : <polyline points="6,9 3,12 6,15" />}
              </svg>
            </button>

            {/* Logo */}
            <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
              {(() => {
                const logoSrc = branding?.logoUrl ? `${API_BASE}/${branding.logoUrl}` : '/berlinr-logo.png';
                return <img src={logoSrc} alt="Neu Berlinr" style={{ height: '68px', width: 'auto' }} />;
              })()}
            </Link>

            {/* Right — clocks + controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', minWidth: 0 }}>
              <div className="hide-mobile">
                <WorldClock />
              </div>
<NotificationBell />
            </div>

          </div>
        </div>
      </header>

      {impersonating && (
        <div style={{
          backgroundColor: '#78350F',
          color: '#FEF3C7',
          fontSize: 'var(--font-size-sm)',
          padding: '7px var(--spacing-md)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--spacing-md)',
        }}>
          <span style={{ opacity: 0.6, fontSize: '11px', letterSpacing: '0.08em', fontWeight: '500' }}>VIEWING AS</span>
          <span>
            <strong>{impersonating.firstName} {impersonating.lastName}</strong>
            {' '}·{' '}
            <span style={{ opacity: 0.8, textTransform: 'capitalize' }}>
              {impersonating.role === 'super_admin' ? 'Super Admin' : impersonating.role === 'admin' ? 'Admin' : 'Employee'}
            </span>
          </span>
          <button
            onClick={stopImpersonation}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#FEF3C7',
              borderRadius: '2px',
              padding: '2px 10px',
              fontSize: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              letterSpacing: '0.03em',
            }}
          >
            Exit
          </button>
        </div>
      )}
    </>
  );
};

export default Header;
